/**
 * User Menu Component
 * 
 * 로그인된 사용자 프로필 + 드롭다운 메뉴
 */

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { analytics } from '../analytics';
import ProfileModal from './ProfileModal';
import SettingsModal from './SettingsModal';
import TeamManagementModal from './TeamManagementModal';
import ApiKeysModal from './ApiKeysModal';
import CouponModal from './CouponModal';

export default function UserMenu() {
  const { t } = useLanguage();
  const { user, logout, quota } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isTeamOpen, setIsTeamOpen] = useState(false);
  const [isApiKeysOpen, setIsApiKeysOpen] = useState(false);
  const [isCouponOpen, setIsCouponOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 메뉴 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const handleLogout = () => {
    analytics.logoutClick();
    logout();
    setIsOpen(false);
  };

  const handleProfileClick = () => {
    analytics.profileClick();
    setIsOpen(false);
    setIsProfileOpen(true);
  };

  const handleSettingsClick = () => {
    analytics.settingsClick?.();
    setIsOpen(false);
    setIsSettingsOpen(true);
  };

  const handleTeamClick = () => {
    analytics.teamManageClick();
    setIsOpen(false);
    setIsTeamOpen(true);
  };

  const handleApiKeysClick = () => {
    analytics.apiKeysClick();
    setIsOpen(false);
    setIsApiKeysOpen(true);
  };

  const handleCouponClick = () => {
    analytics.couponClick();
    setIsOpen(false);
    setIsCouponOpen(true);
  };

  // DevPlanSwitcher 오버라이드 반영: quota.plan 우선 사용
  // 팀에 소속된 사용자는 쿼타 공유로 quota.plan이 'team'이 되므로
  // isTeamPlan만 체크하면 팀 오너와 멤버 모두 커버됨
  const effectivePlan = quota?.plan || user.subscription_plan;
  const isTeamPlan = effectivePlan === 'team';
  const showTeamMenu = isTeamPlan;
  const isPaidPlan = effectivePlan !== 'free';
  
  // Lemon Squeezy Customer Portal URL (환경변수로 설정)
  const billingUrl = import.meta.env.VITE_LEMONSQUEEZY_BILLING_URL || '';

  const handleManageSubscription = () => {
    analytics.subscriptionManageClick();
    if (billingUrl) {
      // 사용자 이메일을 쿼리 파라미터로 전달하면 자동 로그인됨
      const url = `${billingUrl}?prefilled_email=${encodeURIComponent(user.email)}`;
      window.open(url, '_blank');
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* 프로필 버튼 */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 rounded border border-transparent hover:border-terminal-border transition-colors"
      >
        {user.picture ? (
          <img 
            src={user.picture} 
            alt="" 
            className="w-6 h-6 rounded-full"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-6 h-6 rounded-full bg-status-go/20 flex items-center justify-center text-status-go text-xs font-medium">
            {user.name?.charAt(0) || user.email.charAt(0)}
          </div>
        )}
        <span 
          className="text-terminal-text text-sm max-w-[100px] truncate hidden sm:inline"
          title={user.name || user.email}
        >
          {user.name || user.email}
        </span>
        <svg 
          className={`w-4 h-4 text-terminal-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* 드롭다운 메뉴 */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-terminal-bg border-2 border-black dark:border-terminal-border rounded-lg shadow-xl z-[100] overflow-hidden">
          {/* 사용자 정보 - 클릭 시 개인정보 관리 */}
          <button
            onClick={handleProfileClick}
            className="w-full px-4 py-3 border-b-2 border-black/10 dark:border-terminal-border bg-[var(--neo-lime)]/20 dark:bg-terminal-dim/20 hover:bg-[var(--neo-lime)]/40 dark:hover:bg-terminal-dim/40 transition-colors text-left cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p 
                  className="text-black dark:text-terminal-text font-bold text-sm truncate"
                  title={user.name || 'User'}
                >
                  {user.name || 'User'}
                </p>
                <p 
                  className="text-black/60 dark:text-terminal-muted text-xs truncate"
                  title={user.email}
                >
                  {user.email}
                </p>
              </div>
              <svg 
                className="w-4 h-4 text-black/40 dark:text-terminal-muted group-hover:text-black dark:group-hover:text-terminal-text transition-colors ml-2 flex-shrink-0" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
          
          {/* 메뉴 항목 */}
          <div className="py-1">
            {/* 환경설정 */}
            <button
              onClick={handleSettingsClick}
              className="w-full px-4 py-2 text-left text-sm text-black dark:text-terminal-text hover:bg-black/5 dark:hover:bg-terminal-dim/30 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t.settings?.title || '환경설정'}
            </button>

            {/* 팀 관리 - 팀에 속해있거나 Team 플랜이면 표시 */}
            {showTeamMenu && (
              <button
                onClick={handleTeamClick}
                className="w-full px-4 py-2 text-left text-sm text-black dark:text-terminal-text hover:bg-black/5 dark:hover:bg-terminal-dim/30 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {t.team.viewTeam}
              </button>
            )}
            {/* API 키 관리 - 팀에 속해있거나 Team 플랜이면 표시 */}
            {showTeamMenu && (
              <button
                onClick={handleApiKeysClick}
                className="w-full px-4 py-2 text-left text-sm text-black dark:text-terminal-text hover:bg-black/5 dark:hover:bg-terminal-dim/30 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                {t.apiKeys.viewApiKeys}
              </button>
            )}
            {/* 구독 관리 - 유료 플랜만 */}
            {isPaidPlan && billingUrl && (
              <button
                onClick={handleManageSubscription}
                className="w-full px-4 py-2 text-left text-sm text-black dark:text-terminal-text hover:bg-black/5 dark:hover:bg-terminal-dim/30 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                {t.auth.manageSubscription}
              </button>
            )}
            {/* 쿠폰 */}
            <button
              onClick={handleCouponClick}
              className="w-full px-4 py-2 text-left text-sm text-black dark:text-terminal-text hover:bg-black/5 dark:hover:bg-terminal-dim/30 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5H9a2 2 0 00-2 2v2a2 2 0 000 4v2a2 2 0 002 2h6a2 2 0 002-2v-2a2 2 0 000-4V7a2 2 0 00-2-2z" />
              </svg>
              {t.coupon.menu}
            </button>
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 text-left text-sm text-black dark:text-terminal-text hover:bg-black/5 dark:hover:bg-terminal-dim/30 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {t.auth.logout}
            </button>
          </div>
        </div>
      )}

      {/* 개인정보 관리 모달 */}
      <ProfileModal 
        isOpen={isProfileOpen} 
        onClose={() => setIsProfileOpen(false)} 
      />

      {/* 환경설정 모달 */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      {/* 팀 관리 모달 */}
      <TeamManagementModal
        isOpen={isTeamOpen}
        onClose={() => setIsTeamOpen(false)}
      />

      {/* API 키 관리 모달 */}
      <ApiKeysModal
        isOpen={isApiKeysOpen}
        onClose={() => setIsApiKeysOpen(false)}
      />

      {/* 쿠폰 모달 */}
      <CouponModal
        isOpen={isCouponOpen}
        onClose={() => setIsCouponOpen(false)}
      />
    </div>
  );
}

